import {useCallback, useEffect, useMemo, useState} from 'react';

import logError from 'src/utils/errors/logError';

import {firestoreDB} from '@/services/firebase/initializeFirebase';

const appRateCollection = 'appRate';

type IDeviceType = 'desktop' | 'mobile';

interface IAppRateData {
  label: string;
  rate: number;
}

interface IAppRateDocument {
  desktop: IAppRateData | null;
  mobile: IAppRateData | null;
}

interface IUseAppRatesParams {
  documentIds: string[];
  enabled?: boolean;
}

type IAppRatesMap = Record<string, IAppRateDocument>;

const documentCache = new Map<string, IAppRateDocument>();
const documentRequests = new Map<string, Promise<IAppRateDocument>>();

const parseRateValue = (value: unknown): number | null => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }

  if (typeof value === 'string') {
    const parsedRate = Number(value.trim());
    if (Number.isFinite(parsedRate)) {
      return parsedRate;
    }
  }

  return null;
};

const parseRateData = (value: unknown): IAppRateData | null => {
  if (!value || typeof value !== 'object') return null;

  const data = value as {label?: unknown; rate?: unknown; reate?: unknown};
  const parsedRate = parseRateValue(data.rate ?? data.reate);

  if (typeof data.label !== 'string' || parsedRate === null) {
    return null;
  }

  return {
    label: data.label,
    rate: parsedRate,
  };
};

const normalizeRateDocument = (value: unknown): IAppRateDocument => {
  const data = value && typeof value === 'object' ? value : {};
  const appRateData = data as {desktop?: unknown; mobile?: unknown};

  return {
    desktop: parseRateData(appRateData.desktop),
    mobile: parseRateData(appRateData.mobile),
  };
};

const loadAppRateDocument = async (
  documentId: string,
  forceRefetch = false,
): Promise<IAppRateDocument> => {
  if (!forceRefetch) {
    const cachedDocument = documentCache.get(documentId);
    if (cachedDocument) {
      return cachedDocument;
    }
  }

  if (!forceRefetch) {
    const pendingRequest = documentRequests.get(documentId);
    if (pendingRequest) {
      return pendingRequest;
    }
  }

  const request = firestoreDB
    .collection(appRateCollection)
    .doc(documentId)
    .get()
    .then(snapshot => {
      if (!snapshot.exists) {
        const emptyDocument = {desktop: null, mobile: null};
        documentCache.set(documentId, emptyDocument);
        return emptyDocument;
      }

      const normalizedDocument = normalizeRateDocument(snapshot.data());
      documentCache.set(documentId, normalizedDocument);
      return normalizedDocument;
    })
    .finally(() => {
      documentRequests.delete(documentId);
    });

  documentRequests.set(documentId, request);
  return request;
};

export const useAppRates = ({
  documentIds,
  enabled = true,
}: IUseAppRatesParams) => {
  const uniqueDocumentIds = useMemo(
    () => Array.from(new Set(documentIds)),
    [documentIds],
  );

  const [ratesByDocument, setRatesByDocument] = useState<IAppRatesMap>(() => {
    return uniqueDocumentIds.reduce<IAppRatesMap>((accumulator, documentId) => {
      const cachedDocument = documentCache.get(documentId);

      if (cachedDocument) {
        accumulator[documentId] = cachedDocument;
      }

      return accumulator;
    }, {});
  });

  const [isLoading, setIsLoading] = useState(() => {
    if (!enabled || uniqueDocumentIds.length === 0) {
      return false;
    }

    return uniqueDocumentIds.some(documentId => !documentCache.has(documentId));
  });
  const [hasError, setHasError] = useState(false);

  const fetchAppRates = useCallback(
    async (forceRefetch = false) => {
      if (!enabled || uniqueDocumentIds.length === 0) {
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      try {
        setHasError(false);
        const entries = await Promise.all(
          uniqueDocumentIds.map(async documentId => {
            const rateData = await loadAppRateDocument(
              documentId,
              forceRefetch,
            );
            return [documentId, rateData] as const;
          }),
        );

        setRatesByDocument(previousRates => {
          const nextRates = {...previousRates};
          entries.forEach(([documentId, rateData]) => {
            nextRates[documentId] = rateData;
          });

          return nextRates;
        });
      } catch (error) {
        setHasError(true);
        logError(error, {
          fileName: 'useAppRates.ts',
          functionName: 'fetchAppRates',
          customTitle: 'Failed to fetch app rates',
          data: {documentIds: uniqueDocumentIds},
        });
      } finally {
        setIsLoading(false);
      }
    },
    [enabled, uniqueDocumentIds],
  );

  useEffect(() => {
    let isActive = true;

    const syncAppRates = async () => {
      if (!enabled || uniqueDocumentIds.length === 0) {
        if (isActive) {
          setIsLoading(false);
        }
        return;
      }

      setIsLoading(true);
      try {
        setHasError(false);
        const entries = await Promise.all(
          uniqueDocumentIds.map(async documentId => {
            const rateData = await loadAppRateDocument(documentId);
            return [documentId, rateData] as const;
          }),
        );

        if (!isActive) return;

        setRatesByDocument(previousRates => {
          const nextRates = {...previousRates};
          entries.forEach(([documentId, rateData]) => {
            nextRates[documentId] = rateData;
          });
          return nextRates;
        });
      } catch (error) {
        if (!isActive) return;

        setHasError(true);
        logError(error, {
          fileName: 'useAppRates.ts',
          functionName: 'syncAppRates',
          customTitle: 'Failed to synchronize app rates',
          data: {documentIds: uniqueDocumentIds},
        });
      } finally {
        if (isActive) {
          setIsLoading(false);
        }
      }
    };

    syncAppRates();

    return () => {
      isActive = false;
    };
  }, [enabled, uniqueDocumentIds]);

  const appStoreData = useMemo(
    () => ratesByDocument.appStore ?? null,
    [ratesByDocument],
  );
  const trustpilotData = useMemo(
    () => ratesByDocument.trustpilot ?? null,
    [ratesByDocument],
  );

  const getRateByDevice = useCallback(
    (documentId: string, device: IDeviceType) => {
      return ratesByDocument[documentId]?.[device] ?? null;
    },
    [ratesByDocument],
  );

  return {
    ratesByDocument,
    appStoreData,
    trustpilotData,
    isLoading,
    hasError,
    getRateByDevice,
    refetch: () => fetchAppRates(true),
  };
};
